import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import DelayedFallback from "../components/loading/DelayedFallback";
import { requestSessionExpiredLogout } from "../utils/sessionExpiredLogout";

const ExpiredSessionModal = lazy(() =>
  import("./../../src/components/internalAssets/modals/ExpiredSessionModal")
);

const LoggedInCountdownTimer = ({ initialCountdown, logoutAlertTime, handleLogout }) => {
  const [countdown, setCountdown] = useState(initialCountdown);
  const [syncing, setSyncing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [triggeredModal, setTriggeredModal] = useState(false);
  const [deadlineMs, setDeadlineMs] = useState(null);

  const countdownTime = useCallback(() => {
    // Without client-readable tokens, decrement based on current countdown
    return Math.max(0, (typeof countdown === "number" ? countdown : 0) - 1);
  }, [countdown]);

  const resetCountdown = useCallback(() => {
    // Reset to the initial session TTL (should match backend access TTL)
    setCountdown(initialCountdown);
    // Keep the deadline in sync with the reset so ticking stays accurate even after tab throttling
    if (typeof initialCountdown === "number") {
      setDeadlineMs(Date.now() + initialCountdown * 1000);
    }
    setShowModal(false);
    setTriggeredModal(false);
  }, [initialCountdown]);

  // Sync countdown with server token exp on mount and occasionally thereafter
  const syncSession = useCallback(async () => {
    try {
      setSyncing(true);
      const res = await fetch("/api/auth/session", { credentials: "include" });
      if (res.status === 401) {
        requestSessionExpiredLogout();
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      if (!data?.ok) return;
      if (typeof data.accessRemainingSec === "number") {
        setCountdown(data.accessRemainingSec);
        setDeadlineMs(Date.now() + data.accessRemainingSec * 1000);
      }
    } catch {}
    finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await syncSession();
    };
    run();
    const id = setInterval(run, 60_000); // re-sync every minute to counter drift
    return () => { cancelled = true; clearInterval(id); };
  }, [syncSession]);

  // Force an immediate sync when the tab becomes visible to correct any throttled timers
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncSession();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [syncSession]);

  // Drive countdown by deadline so time remains accurate across hidden tabs/throttling
  useEffect(() => {
    if (!deadlineMs) return;
    let intervalId = null;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) {
        if (intervalId) clearInterval(intervalId);
        handleLogout();
      }
    };
    // Do an immediate tick so UI reflects latest state
    tick();
    intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [deadlineMs, handleLogout]);

  useEffect(() => {
    if (countdown && countdown <= logoutAlertTime && !showModal && !triggeredModal) {
      setShowModal(true);
      setTriggeredModal(true);
    } else if (countdown && countdown > logoutAlertTime && showModal) {
      setShowModal(false);
      setTriggeredModal(false);
    }
  }, [countdown, logoutAlertTime, showModal, triggeredModal]);

  return (
    <>
      {showModal && (
        <Suspense fallback={<DelayedFallback />}>
          <ExpiredSessionModal
            onClose={() => {
              setShowModal(false);
              setTriggeredModal(false);
            }}
            showModal={showModal}
            countdown={countdown}
            countdownTime={countdownTime}
            resetCountdown={resetCountdown}
            handleLogout={handleLogout}
          />
        </Suspense>
      )}
    </>
  );
};

export default LoggedInCountdownTimer;
